/**
 * Cross-cutting state used to inject snap correction into the resize flow.
 *
 * `<NodeResizer>` calls our `onResize` BEFORE it dispatches dimension/position
 * changes to the store. So if we override the store from inside `onResize`, the
 * dimension dispatch immediately overwrites our snapped values.
 *
 * Workaround: the resize-snap hook records its snap target here, and the store's
 * `onNodesChange` swaps dimension/position changes for the resizing node with the
 * snapped values. This lands the snapped values in the store on the same frame
 * the unsnapped change was about to.
 *
 * Single-slot map: only one node resizes at a time (no multi-select resize).
 */
export type ResizeSnapTarget = {
  x: number;
  y: number;
  width: number;
  height: number;
};

let active: { nodeId: string; target: ResizeSnapTarget } | null = null;

export function setResizeSnapTarget(nodeId: string, target: ResizeSnapTarget): void {
  active = { nodeId, target };
}

export function clearResizeSnapTarget(nodeId: string): void {
  if (active?.nodeId === nodeId) {
    active = null;
  }
}

export function getResizeSnapTarget(nodeId: string): ResizeSnapTarget | null {
  return active?.nodeId === nodeId ? active.target : null;
}
